import React, { useId, useRef, useState } from 'react';
import { useI18n } from '../i18n/index.jsx';

// Ordered photo list for the product wizard. Items are either already
// saved ({ id, url }) or picked locally ({ tempId, file, url: object URL }).
// The first photo is the main photo. Drag a photo (mouse, or the ⠿ handle
// on touch screens; arrow keys on the handle) to reorder. Photos are
// resized in the browser when the product is saved (ListingForm).
// onCrop(index): shows a ✂ button on each photo (the wizard opens the cropper).
export const MAX_PHOTOS = 8;
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function PhotoUploader({ items, onChange, disabled, onCrop }) {
  const { t } = useI18n();
  const inputId = useId();
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(null);
  const [dropHover, setDropHover] = useState(false);
  const drag = useRef(null);
  const key = (p) => p.id ?? p.tempId;

  const add = (fileList) => {
    setError('');
    const files = Array.from(fileList || []);
    const bad = files.filter((f) => !TYPES.includes(f.type));
    const big = files.filter((f) => TYPES.includes(f.type) && f.size > MAX_PHOTO_BYTES);
    const ok = files.filter((f) => TYPES.includes(f.type) && f.size <= MAX_PHOTO_BYTES);
    const room = MAX_PHOTOS - items.length;
    const msgs = [];
    if (bad.length) msgs.push(t('listingForm.photoType', { names: bad.map((f) => f.name).join(', ') }));
    if (big.length) msgs.push(t('listingForm.photoTooBig', { names: big.map((f) => f.name).join(', ') }));
    if (ok.length > room) msgs.push(t('listingForm.photoLimit', { n: MAX_PHOTOS }));
    setError(msgs.join(' '));
    const picked = ok.slice(0, room).map((file) => ({
      tempId: `new-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`,
      file,
      url: URL.createObjectURL(file),
    }));
    if (picked.length) onChange([...items, ...picked]);
  };
  const moveTo = (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, j) => j !== i));

  // Pointer drag: works with mouse, pen and touch (touch only from the handle,
  // so swiping over the photos still scrolls the page).
  const down = (e, i) => {
    if (disabled || e.button > 0) return;
    if (e.target.closest('button:not(.photo-handle)')) return;
    if (e.pointerType !== 'mouse' && !e.target.closest('.photo-handle')) return;
    e.preventDefault();
    drag.current = { index: i };
    setDragging(i);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    const over = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-photo-index]');
    const to = over ? Number(over.dataset.photoIndex) : null;
    if (to !== null && to !== drag.current.index) {
      moveTo(drag.current.index, to);
      drag.current.index = to;
      setDragging(to);
    }
  };
  const up = () => { drag.current = null; setDragging(null); };

  return (
    <div>
      <div className="photo-uploader" onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        {items.map((p, i) => (
          <div className={`photo-tile${dragging === i ? ' is-dragging' : ''}`} key={key(p)} data-photo-index={i} onPointerDown={(e) => down(e, i)}>
            <img src={p.url} alt={t('listing.photo', { n: i + 1, total: items.length })} draggable="false" />
            {i === 0 && <span className="photo-cover">★ {t('listingForm.cover')}</span>}
            <button type="button" className="photo-remove" onClick={() => remove(i)} disabled={disabled} aria-label={t('listingForm.remove')}>×</button>
            <div className="photo-bar">
              <button type="button" className="photo-handle" aria-label={t('listingForm.dragHandle', { n: i + 1 })} disabled={disabled}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); moveTo(i, i - 1); }
                  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); moveTo(i, i + 1); }
                }}>⠿</button>
              {onCrop && <button type="button" className="photo-main photo-crop" onClick={() => onCrop(i)} disabled={disabled} title={t('wizard.crop')} aria-label={t('wizard.cropN', { n: i + 1 })}>✂</button>}
              {i > 0 && <button type="button" className="photo-main" onClick={() => moveTo(i, 0)} disabled={disabled} title={t('listingForm.setMainLong')} aria-label={t('listingForm.setMainLong')}>{t('listingForm.setMain')}</button>}
            </div>
          </div>
        ))}
        {items.length < MAX_PHOTOS && (
          <label className={`photo-add${disabled ? ' is-disabled' : ''}${dropHover ? ' is-hover' : ''}`} htmlFor={inputId}
            onDragOver={(e) => { e.preventDefault(); setDropHover(true); }} onDragLeave={() => setDropHover(false)}
            onDrop={(e) => { e.preventDefault(); setDropHover(false); if (!disabled) add(e.dataTransfer.files); }}>
            <span aria-hidden="true">+</span>
            {t('listingForm.addPhotos')}
            <span className="photo-add-hint">{t('listingForm.dropHint')}</span>
            <input id={inputId} type="file" accept={TYPES.join(',')} multiple disabled={disabled} className="sr-only"
              onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
          </label>
        )}
      </div>
      <p className="field-hint">{t('listingForm.photoCount', { n: items.length, max: MAX_PHOTOS })}</p>
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}
